import { useState } from 'react'
import { Link } from 'react-router-dom'
import Masonry from 'react-masonry-css'
import { useGalleryList } from '../../../hooks/useGallery'
import { useGeneralSettings } from '../../../hooks/useGeneralSettings'

const BREAKPOINTS = { default: 3, 1024: 3, 768: 2, 640: 1 }

function GalleryCardSkeleton({ tall }) {
  return (
    <div className={`mb-4 rounded-2xl border border-[#C8922A]/10 bg-[#1C140A]/5 animate-pulse ${tall ? 'h-96' : 'h-64'}`} />
  )
}

function GalleryCard({ album }) {
  const [status, setStatus] = useState('loading')
  const loaded = status === 'loaded'

  const mediaLabel = [
    album.totalImages ? `${album.totalImages} PHOTO${album.totalImages === 1 ? '' : 'S'}` : null,
    album.totalVideos ? `${album.totalVideos} VIDEO${album.totalVideos === 1 ? '' : 'S'}` : null,
  ].filter(Boolean).join(' · ') || 'GALLERY'

  return (
    <Link
      to={`/gallery/${album.id}`}
      className="group relative mb-4 block cursor-pointer overflow-hidden rounded-2xl border border-[#C8922A]/15 bg-[#1C140A] shadow-sm transition-all duration-500 hover:border-[#C8922A]/50 hover:shadow-2xl hover:shadow-[#C8922A]/20"
    >
      <div className={`relative ${loaded ? '' : 'aspect-[4/5]'}`}>
        {/* Placeholder shown while the cover loads, and permanently if it fails —
            so the card (title/media count) is always visible instead of collapsing. */}
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#2a2018] to-[#1C140A]">
            {status === 'loading' ? (
              <span className="w-8 h-8 rounded-full border-2 border-[#E6C878]/30 border-t-[#E6C878] animate-spin" />
            ) : (
              <span className="text-[#E6C878]/30 text-3xl">✦</span>
            )}
          </div>
        )}

        {album.coverImage && (
          <img
            src={album.coverImage}
            alt={album.title}
            className={
              loaded
                ? 'block w-full h-auto object-cover transition-transform duration-700 group-hover:scale-110'
                : 'absolute inset-0 w-full h-full object-cover opacity-0'
            }
            loading="lazy"
            onLoad={() => setStatus('loaded')}
            onError={() => setStatus('error')}
          />
        )}

        {/* Legibility scrim for the overlaid caption */}
        <div className="absolute inset-0 bg-linear-to-t from-[#1C140A]/90 via-[#1C140A]/15 to-transparent" />
        {/* Hairline gold inner frame */}
        <div className="pointer-events-none absolute inset-2.5 rounded-xl border border-white/15 transition-colors duration-500 group-hover:border-[#E6C878]/40" />

        <span className="absolute top-5 left-5 inline-flex items-center gap-1.5 rounded-full bg-black/35 backdrop-blur-md border border-white/15 px-3.5 py-1.5 text-[8px] tracking-[0.25em] text-[#E6C878]">
          <span className="text-[9px]">✦</span> {mediaLabel}
        </span>

        {album.totalVideos > 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-md border border-[#E6C878]/40 flex items-center justify-center text-[#E6C878] shadow-2xl transition-all duration-500 group-hover:scale-110 group-hover:bg-[#C8922A] group-hover:text-[#1C140A] group-hover:border-transparent">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 ml-0.5">
                <polygon points="6 3 20 12 6 21 6 3" />
              </svg>
            </div>
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 p-5 pt-10">
          <div className="flex items-center gap-2 mb-2.5">
            <span className="h-px w-6 bg-[#E6C878]/60" />
            <span className="text-[8px] tracking-[0.3em] text-[#E6C878]/80">
              {album.totalVideos > 0 ? 'WATCH DISCOURSE' : 'SACRED CAPTURE'}
            </span>
          </div>
          <div className="flex items-end justify-between gap-3">
            <h3 className="text-base md:text-lg font-cinzel text-white leading-snug line-clamp-2 transition-colors duration-500 group-hover:text-[#E6C878]">
              {album.title}
            </h3>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mb-1 shrink-0 text-[#E6C878] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500">
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </Link>
  )
}

export default function GalleryMasonrySection() {
  const [page, setPage] = useState(1)
  const [items, setItems] = useState([])

  const { settings } = useGeneralSettings()
  const youtubeUrl = typeof settings.youtube === 'string' ? settings.youtube.trim() : ''

  const { items: pageItems, lastPage, isPending, isFetching, isError, refetch } = useGalleryList(page)

  const allItems = page === 1 ? pageItems : [...items, ...pageItems]

  function handleLoadMore() {
    setItems(allItems)
    setPage((p) => p + 1)
  }

  return (
    <section id="gallery" className="py-16 md:py-24 md:px-18 overflow-hidden font-normal uppercase" style={{ background: '#FDFAF6' }}>
      <div className="container px-6 mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-8">
          <div className="max-w-3xl w-full text-left">
            <span className="text-secondary text-[10px] tracking-[0.3em] uppercase mb-4 block font-normal">SACRED ARCHIVE</span>
            <h2 className="text-3xl md:text-5xl font-cinzel text-[#1C140A] leading-tight mb-6 font-normal uppercase">
              MOMENTS OF <span className="text-secondary">DIVINITY</span>
            </h2>
            <p className="text-[#8B7355] leading-relaxed text-lg font-normal uppercase">
              A LIVING COLLECTION OF TEACHINGS, RITUALS AND SACRED GATHERINGS — CAPTURED ACROSS THE JOURNEY OF MAHARAJ JI.
            </p>
          </div>
          {youtubeUrl && (
            <a
              href={youtubeUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center whitespace-nowrap bg-[#1C140A] py-2 rounded-full px-8 h-11 text-white hover:bg-[#C8922A] transition-all duration-500 font-normal uppercase tracking-widest text-[11px] shrink-0"
            >
              VIEW YOUTUBE CHANNEL
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 ml-2">
                <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
                <path d="m10 15 5-3-5-3z" />
              </svg>
            </a>
          )}
        </div>

        {isError && (
          <div className="text-center py-16">
            <p className="text-[#8B7355] mb-4 normal-case">Couldn't load the gallery right now.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center justify-center whitespace-nowrap bg-[#1C140A] py-2 rounded-full px-8 h-11 text-white hover:bg-[#C8922A] transition-all duration-500 font-normal uppercase tracking-widest text-[11px]"
            >
              Try again
            </button>
          </div>
        )}

        {!isError && isPending && (
          <Masonry breakpointCols={BREAKPOINTS} className="flex w-auto -ml-4" columnClassName="pl-4 bg-clip-padding">
            {Array.from({ length: 9 }).map((_, i) => (
              <GalleryCardSkeleton key={i} tall={i % 3 === 1} />
            ))}
          </Masonry>
        )}

        {!isError && !isPending && allItems.length === 0 && (
          <p className="text-center text-[#8B7355] py-16 normal-case">No gallery items yet — check back soon.</p>
        )}

        {!isError && !isPending && allItems.length > 0 && (
          <>
            <Masonry breakpointCols={BREAKPOINTS} className="flex w-auto -ml-4" columnClassName="pl-4 bg-clip-padding">
              {allItems.map((album) => (
                <GalleryCard key={album.id} album={album} />
              ))}
            </Masonry>

            {page < lastPage && (
              <div className="flex justify-center mt-8">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={isFetching}
                  className="inline-flex items-center justify-center whitespace-nowrap bg-[#1C140A] py-2 rounded-full px-8 h-11 text-white hover:bg-[#C8922A] transition-all duration-500 font-normal uppercase tracking-widest text-[11px] disabled:opacity-50"
                >
                  {isFetching ? 'Loading…' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  )
}
